import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/contexts/UserContext";
import { teamsService } from "@/services/teamsService";
import { notificationsService } from "@/services/notificationsService";

interface CelebrateModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: CelebrationData) => void;
}

export interface CelebrationData {
  message: string;
  emoji: string;
  shareWithTeam: boolean;
}

const EMOJIS = ["🎉", "💪", "✅", "⭐", "🚀", "🏆", "💯", "🙌"];

const CelebrateModal = ({
  isOpen,
  onOpenChange,
  onSubmit,
}: CelebrateModalProps) => {
  const { toast } = useToast();
  const { user } = useUser();
  const [message, setMessage] = useState("");
  const [selectedEmoji, setSelectedEmoji] = useState("🎉");
  const [shareWithTeam, setShareWithTeam] = useState(true);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter what you're celebrating",
        variant: "destructive",
      });
      return;
    }

    const data: CelebrationData = {
      message,
      emoji: selectedEmoji,
      shareWithTeam,
    };

    if (shareWithTeam && user && user.teamId) {
      console.log("ajouté en bd");
      try {
        // Get all team members via teamsService
        const response = await teamsService.getMembers(user.teamId);
        if (!response.success) {
          console.error("Error fetching team members:", response.error);
          return;
        }

        const teamMembers = response.data || [];
        // Send notification to each team member
        for (const member of teamMembers) {
          if (member.id !== user.id) {
            await notificationsService.create({
              type: "team_alert",
              title: "Team Celebration",
              message: `${user.name} is celebrating: ${selectedEmoji} ${message}`,
              priority: 'normal',
              data: { type: "celebration" },
              link: "/dashboard"
            });
          }
        }

        toast({
          title: "Celebration shared!",
          description: "Your celebration has been shared with the team.",
        });

        if (onSubmit) {
          onSubmit(data);
        }

        onOpenChange(false);
      } catch (error) {
        console.error("Error sending team notifications:", error);
        toast({
          title: "Error",
          description: "Failed to send notifications to team members",
          variant: "destructive",
        });
      }
    } else {
      if (onSubmit) {
        onSubmit(data);
      }
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Celebrate Progress</DialogTitle>
          <DialogDescription>
            Share and celebrate your achievements.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div>
            <Label htmlFor="celebration" className="mb-2 block">
              What are you celebrating?
            </Label>
            <Input
              id="celebration"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Completed Q2 design system!"
            />
          </div>

          <div>
            <Label className="mb-2 block">Choose an emoji</Label>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map((emoji) => (
                <Button
                  key={emoji}
                  type="button"
                  variant={selectedEmoji === emoji ? "default" : "outline"}
                  className="w-10 h-10 text-xl"
                  onClick={() => setSelectedEmoji(emoji)}
                >
                  {emoji}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Switch
              id="share-team"
              checked={shareWithTeam}
              onCheckedChange={setShareWithTeam}
            />
            <Label htmlFor="share-team">Share with team</Label>
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" onClick={handleSubmit}>
            {shareWithTeam ? "Share & Save" : "Save"} Celebration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CelebrateModal;
